import useHasAccess from "@hooks/useHasAccess"
import useUser from "@hooks/useUser"

import getObjectDetailsRoute from "@utils/getObjectDetailsRoute"

import useObjectsView from "@organisms/ObjectsView/ObjectsView.context"
import { InfiniteObjectsList, ObjectsData } from "@organisms/ObjectsView/ObjectsView.types"
import { GetObjectDetailsRouteFunction } from "@organisms/ObjectsView/ObjectsViewControls/ObjectsViewTab/ObjectsViewTab.types"

import { PERMISSION_LEVEL } from "@constants/permissionLevel"

/** The list props the table and cards organisms share, read from the surrounding ObjectsView.Provider. */
export default function useInfiniteObjectsList<T extends ObjectsData = ObjectsData>(): InfiniteObjectsList<T> {
    const { data, hasNextPage, fetchNextPage, isFetchingNextPage, objectIndexName, isRowClickable, onRowHoverChange } =
        useObjectsView<T>()

    const { user } = useUser()
    const { hasPermission } = useHasAccess()

    const getObjectRoute = getObjectDetailsRoute({
        objectName: objectIndexName,
        hasDraftEstimateEditPermission: hasPermission("estimates_edit_permission", PERMISSION_LEVEL.RESTRICTED),
        hasDraftJobEditPermission: hasPermission("jobs_edit_permission", PERMISSION_LEVEL.RESTRICTED),
        hasDraftInvoiceEditPermission: hasPermission("invoices_edit_permission", PERMISSION_LEVEL.RESTRICTED),
        hasDraftPurchaseOrderEditPermission: hasPermission(
            "purchase_orders_edit_permission",
            PERMISSION_LEVEL.RESTRICTED,
        ),
        hasDraftBillEditPermission: hasPermission("bills_edit_permission", PERMISSION_LEVEL.RESTRICTED),
        serviceCompanySlug: user?.service_company?.slug || "",
    }) as GetObjectDetailsRouteFunction

    return {
        rows: data,
        isPaginated: true,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
        getRowRoute: isRowClickable ? getObjectRoute : undefined,
        onRowHoverChange,
    }
}
